import { useState } from "react";
import { useTranslation } from "react-i18next";
import LegalPageLayout from "../common/LegalPageLayout";

// Explanatory content lives in locales/{es,en,de}/translation.json under
// "legal_pages.data_deletion_request" (see LegalPageLayout.js for the shape).
// Below that, this page reuses the same contact endpoint as ContactUs.js
// (POST /api/users/contact/) so a visitor can actually submit a deletion
// request from the page itself, not just read a mailto address.
const DataDeletionRequest = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(t("all_fields_required"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/users/contact/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: t("legal_pages.data_deletion_request.form_subject"),
          message: message.trim() || t("legal_pages.data_deletion_request.form_subject"),
          email: email,
          send_copy: true,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t("failed_to_send_message"));
      }

      setIsSubmitted(true);
    } catch (err) {
      console.error("Error:", err);
      setError(err.message || t("error_occurred"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LegalPageLayout translationKey="legal_pages.data_deletion_request">
      <section className="mt-2 mb-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg md:text-xl font-semibold mb-4">
          {t("legal_pages.data_deletion_request.form_title")}
        </h2>

        {isSubmitted ? (
          <p className="text-green-700">{t("message_sent_success")}</p>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-md">
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>
            )}

            <div className="mb-4">
              <label className="block text-gray-700 font-bold mb-2">{t("email")}</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t("enter_your_email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-bold mb-2">{t("message")}</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
                placeholder={t("legal_pages.data_deletion_request.form_placeholder")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full bg-blue-500 text-white py-2 px-4 rounded-lg font-bold hover:bg-blue-600 transition-colors ${
                isLoading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? t("sending") + "..." : t("legal_pages.data_deletion_request.form_submit")}
            </button>
          </form>
        )}
      </section>
    </LegalPageLayout>
  );
};

export default DataDeletionRequest;
